import axios from "axios";
import config from "../../config";

const baseUrl = config.baseUrl;
const endpoint = "/api/informe-titulares";

// ─── Paginación por cursor ────────────────────────────────────────────────────

/**
 * Descarga TODOS los registros en bloques para exportar a Excel.
 * @param {function} onProgress - callback(porcentaje, filasCargadas, total)
 * @param {number}   total      - total de registros (para calcular progreso)
 * @param {number}   blockSize  - registros por bloque (default 500)
 */
export const obtenerTodosInformeTitularesEnBloques = async (
  onProgress,
  total = 0,
  blockSize = 500
) => {
  const todos = [];
  let lastId  = "";
  let hasMore = true;

  while (hasMore) {
    const resultado = await obtenerInformeTitularesPaginado(lastId, blockSize);
    todos.push(...resultado.data);

    hasMore = resultado.hasMore;
    lastId  = resultado.nextCursor ?? "";

    const porcentaje = total > 0
      ? Math.min(Math.round((todos.length / total) * 100), 99)
      : null;

    onProgress?.(porcentaje, todos.length, total);
  }

  return todos;
};

export const obtenerInformeTitularesPaginado = async (lastId = "", pageSize = 30) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}`, {
      params: { lastId, pageSize },
    });
    return response.data; // { data, nextCursor, hasMore }
  } catch (error) {
    console.error("Error obteniendo informe titulares paginado:", error);
    throw error;
  }
};

export const obtenerTotalInformeTitulares = async () => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/total`);
    return response.data.total;
  } catch (error) {
    console.error("Error obteniendo total informe titulares:", error);
    throw error;
  }
};

// ─── Filtros ──────────────────────────────────────────────────────────────────

export const obtenerInformeTitularesPorDocumento = async (documento) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/documento/${documento}`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo informe por documento:", error);
    throw error;
  }
};

export const obtenerInformeTitularesPorMunicipio = async (municipio) => {
  try {
    const response = await axios.get(
      `${baseUrl}${endpoint}/municipio/${encodeURIComponent(municipio)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo informe por municipio:", error);
    throw error;
  }
};

export const obtenerInformeTitularesPorDepartamento = async (departamento) => {
  try {
    const response = await axios.get(
      `${baseUrl}${endpoint}/departamento/${encodeURIComponent(departamento)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo informe por departamento:", error);
    throw error;
  }
};

/**
 * @param {"REALIZADA"|"NO-REALIZADA"} estado
 */
export const obtenerInformeTitularesPorEncuesta = async (estado) => {
  try {
    const response = await axios.get(
      `${baseUrl}${endpoint}/encuesta/${encodeURIComponent(estado)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo informe por encuesta:", error);
    throw error;
  }
};

/**
 * @param {"PARTICIPA"|"NO-PARTICIPA"|"SIN-DEFINIR"} estado
 */
export const obtenerInformeTitularesPorParticipacion = async (estado) => {
  try {
    const response = await axios.get(
      `${baseUrl}${endpoint}/participacion/${encodeURIComponent(estado)}`
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo informe por participación:", error);
    throw error;
  }
};

/**
 * @param {"SI"|"NO"} valor
 */
export const obtenerInformeTitularesPorBeneficiario = async (valor) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/beneficiario/${valor}`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo informe por beneficiario:", error);
    throw error;
  }
};

// ─── Catálogos ────────────────────────────────────────────────────────────────

export const obtenerMunicipiosInforme = async () => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/municipios`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo municipios:", error);
    throw error;
  }
};

export const obtenerDepartamentosInforme = async () => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/departamentos`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo departamentos:", error);
    throw error;
  }
};


/**
 * Filtra por encuestador (promotor) usando su ID.
 * @param {number} encuestadorId - ID del encuestador
 */
export const obtenerInformeTitularesPorEncuestador = async (encuestadorId) => {
  try {
    const response = await axios.get(
      `${baseUrl}${endpoint}/encuestador/${encuestadorId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo informe por encuestador:", error);
    throw error;
  }
};

/**
 * Filtra por aprobación de pago.
 * @param {"SI"|"NO"} valor
 */
export const obtenerInformeTitularesPorAprobacionPago = async (valor) => {
  try {
    const response = await axios.get(
      `${baseUrl}${endpoint}/aprobacion-pago/${valor}`
    );
    return response.data;
  } catch (error) {
    console.error("Error obteniendo informe por aprobación de pago:", error);
    throw error;
  }
};

/**
 * Obtiene la lista de encuestadores disponibles para poblar el selector.
 * @returns {{ success: boolean, data: Array<{ encuestador_id, encuestador_nombres, encuestador_apellidos }> }}
 */
export const obtenerEncuestadoresInforme = async () => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/encuestadores`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo encuestadores del informe:", error);
    throw error;
  }
};